/** Parse une date ISO ou MySQL DATETIME en Date locale fiable. */
export function parseDateValue(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value) ? null : value;
  }

  let raw = String(value).trim();
  if (!raw) return null;

  /**
   * Cas Sequelize / MySQL :
   * La BDD stocke souvent "2026-04-30 09:30:00"
   * mais Sequelize renvoie parfois "2026-04-30T09:30:00.000Z".
   *
   * Pour date_contact, on veut afficher l’heure métier saisie,
   * pas convertir depuis UTC vers Europe/Paris.
   *
   * Donc on retire le Z et les millisecondes.
   */
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(raw)) {
    raw = raw.replace(/\.\d{3}Z$/, "");
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(raw)) {
    raw = raw.replace(/Z$/, "");
  }

  // MySQL DATETIME local : "2026-04-30 09:30:00"
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(raw)) {
    raw = raw.replace(" ", "T");
  }

  const d = new Date(raw);
  return isNaN(d) ? null : d;
}

/** Format FR JJ/MM/AAAA HH:MM à partir d'un ISO ou d'une date parseable. */
export function fmtDateFR(iso) {
  const d = parseDateValue(iso);
  if (!d) return "";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const ii = String(d.getMinutes()).padStart(2, "0");

  return `${dd}/${mm}/${yyyy} ${hh}:${ii}`;
}

/** Variante sans l’heure. */
export function fmtDateFRDay(iso) {
  const d = parseDateValue(iso);
  if (!d) return "";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();

  return `${dd}/${mm}/${yyyy}`;
}

/** Date locale YYYY-MM-DD. */
export function todayLocalISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Date locale MySQL friendly :
 * "2026-04-30 14:25:08"
 *
 * Important :
 * - évite le décalage UTC de toISOString()
 * - pratique pour un champ DATETIME MySQL
 */
export function nowLocalDateTimeSql() {
  const d = new Date();

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

/** Convertit une date quelconque en valeur pour input datetime-local. */
export function toInputDateTime(value) {
  const d = parseDateValue(value);
  if (!d) return "";

  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate(),
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Convertit une valeur datetime-local en DATETIME MySQL local. */
export function inputDateTimeToSql(value) {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  // datetime-local donne "YYYY-MM-DDTHH:mm"
  const withSpace = raw.replace("T", " ");

  // Si pas de secondes, on ajoute :00
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}$/.test(withSpace)) {
    return `${withSpace}:00`;
  }

  return withSpace;
}

/** Convertit une URL relative/host-only en URL absolue https:// */
export function toAbsolute(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url.replace(/^\/+/, "")}`;
}

/** Vérifie grossièrement le format d’un email. */
export function isValidEmail(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
}

/** Normalise un email. */
export function normalizeEmail(email) {
  return String(email || "")
    .trim()
    .toLowerCase();
}

/**
 * Extrait et dédoublonne les emails depuis une chaîne libre.
 */
export function parseEmails(val) {
  if (!val) return [];

  const raw = String(val);
  const chunks = raw
    .split(/[;,]/g)
    .map((s) => s.trim())
    .filter(Boolean);

  const found = [];

  for (const c of chunks) {
    const matches =
      c.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}/g) || [];

    for (const m of matches) {
      const e = m.trim();
      if (isValidEmail(e)) found.push(e);
    }
  }

  const seen = new Set();
  const uniques = [];

  for (const e of found) {
    const k = e.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      uniques.push(e);
    }
  }

  return uniques;
}

/** CSV parser simple UTF-8, séparateur virgule, guillemets doubles. */
export function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];

  const headers = splitCsvLine(lines[0]).map((h) => h.trim());
  const out = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i]);
    const row = {};

    headers.forEach((h, idx) => {
      row[h] = cols[idx] ?? "";
    });

    if (row.nb_licencies) {
      row.nb_licencies = Number(row.nb_licencies) || null;
    }

    if (row.date_contact) {
      const d = parseDateValue(row.date_contact);
      row.date_contact = d ? nowLikeSqlFromDate(d) : row.date_contact;
    }

    out.push(row);
  }

  return out;
}

function nowLikeSqlFromDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

function splitCsvLine(line) {
  const result = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const c = line[i];

    if (inQuotes) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        cur += c;
      }
    } else {
      if (c === '"') {
        inQuotes = true;
      } else if (c === ",") {
        result.push(cur);
        cur = "";
      } else {
        cur += c;
      }
    }
  }

  result.push(cur);
  return result;
}
